import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { login, forgotPassword } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/20 focus:border-[#D4AF37] outline-none py-3 font-body text-[#FDFBF7] placeholder:text-[#A89F91]/60 transition-colors duration-300";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [fpEmail, setFpEmail] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!fpEmail) return toast.error("Please enter your email.");
    setFpLoading(true);
    try {
      await forgotPassword(fpEmail);
      setFpSent(true);
      toast.success("If an account exists, a reset link has been sent to your email.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pof flex items-center justify-center px-6">
      <div className="absolute top-8 left-8">
        <Link to="/" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors">
          <ArrowLeft size={15} /> Back to Site
        </Link>
      </div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="Palace of Flavor" className="h-24 w-auto mx-auto mb-6" />
          <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37]">Admin Portal</p>
          <h1 className="font-display text-4xl text-[#FDFBF7] mt-2">{mode === "login" ? "Welcome Back" : "Reset Password"}</h1>
        </div>

        <AnimatePresence mode="wait">
          {mode === "login" ? (
            <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={submit} data-testid="admin-login-form" className="space-y-8">
              <input data-testid="admin-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input data-testid="admin-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              {error && <p data-testid="admin-login-error" className="font-body text-sm text-[#C92A6B]">{error}</p>}
              <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-3 px-10 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }} className="w-full text-center font-body text-xs tracking-[0.15em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors">
                Forgot password?
              </button>
            </motion.form>
          ) : (
            <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {fpSent ? (
                <div className="text-center space-y-6">
                  <p data-testid="forgot-sent-msg" className="font-body text-[#A89F91] leading-relaxed">
                    If an account exists for <span className="text-[#FDFBF7]">{fpEmail}</span>, we've sent a password reset link. Please check your inbox (and spam). The link expires in 1 hour.
                  </p>
                  <button data-testid="forgot-back-login" onClick={() => { setMode("login"); setFpSent(false); }} className="font-body text-xs tracking-[0.15em] uppercase text-[#D4AF37] hover:underline">
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} data-testid="forgot-password-form" className="space-y-8">
                  <p className="font-body text-sm text-[#A89F91] text-center -mt-2">Enter your admin email and we'll send you a reset link.</p>
                  <input data-testid="forgot-email" type="email" className={inputCls} placeholder="Email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} />
                  <button data-testid="forgot-submit" type="submit" disabled={fpLoading} className="w-full flex items-center justify-center gap-3 px-10 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60">
                    {fpLoading && <Loader2 size={16} className="animate-spin" />}
                    {fpLoading ? "Sending..." : "Send Reset Link"}
                  </button>
                  <button type="button" data-testid="forgot-cancel" onClick={() => setMode("login")} className="w-full text-center font-body text-xs tracking-[0.15em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors">
                    Back to sign in
                  </button>
                </form>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
